import { LuChevronDown } from 'react-icons/lu';

import { Button } from '~components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~components/ui/dropdown-menu';
import { cn } from '~utils/cn';

/* ---------------------------------- Types --------------------------------- */

type FilterOption = {
  /** The value this option represents */
  value: string;
  /** The text shown for this option */
  label: string;
};

type FilterDropdownProps = {
  /** Base label shown on the trigger pill before any selected value */
  label: string;
  /** The currently selected value, or null when nothing is selected */
  value: string | null;
  /** The options to choose from */
  options: FilterOption[];
  /** Callback fired when an option is selected, or null to reset/clear */
  onSelect: (value: string | null) => void;
  /**
   * Overrides the text appended after the label when describing the current
   * selection. When omitted, the selected option's label is used. Pass null
   * to show only `label` even while a value is selected.
   */
  displayValue?: string | null;
  /** Alignment of the menu relative to the trigger. Defaults to 'start' */
  align?: 'start' | 'center' | 'end';
  /** Label for the reset ("all") row at the top of the menu. Defaults to 'All' */
  allLabel?: string;
  /** Optional className applied to the trigger pill */
  className?: string;
};

/* -------------------------------- Component ------------------------------- */

/**
 * A single-select filter dropdown rendered as a pill-shaped trigger that opens
 * a checkable menu. Built on the canonical `ui/dropdown-menu` primitive and the
 * `ui/button` pill trigger so every filter across the app shares one look.
 *
 * The trigger reads `{label}` on its own when nothing is selected, or
 * `{label}: {displayValue ?? selected option label}` once a value is chosen.
 */
const FilterDropdown = ({
  label,
  value,
  options,
  onSelect,
  displayValue,
  align = 'start',
  allLabel = 'All',
  className,
}: FilterDropdownProps) => {
  const selectedOption = options.find((option) => option.value === value);
  const suffix = displayValue !== undefined ? displayValue : (selectedOption?.label ?? null);
  const triggerText = suffix ? `${label}: ${suffix}` : label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={value ? 'secondary' : 'outline'} size="sm" className={cn('rounded-full', className)}>
          {triggerText}
          <LuChevronDown aria-hidden="true" className="opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuCheckboxItem checked={value === null} onCheckedChange={() => onSelect(null)}>
          {allLabel}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={value === option.value}
            onCheckedChange={(checked) => onSelect(checked ? option.value : null)}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/* --------------------------------- Exports -------------------------------- */

export type { FilterDropdownProps, FilterOption };
export { FilterDropdown };
